import type {
  AuditAction,
  AuditActivityChange,
  AuditActivityItem,
  AuditSubjectType,
  ListAuditActivitiesParams,
} from '@/services';

/**
 * Recorte ativo da consulta.
 *
 * Não é preciosismo de UI: o BFF escolhe o índice do DynamoDB por PRECEDÊNCIA
 * (entidade > autor > dia) e ignora os demais recortes. Consultar "autor X no
 * dia Y" devolveria TODOS os dias do autor X, sem avisar. Por isso a tela
 * expõe um recorte de cada vez em vez de filtros combináveis.
 */
export type AuditScopeMode = 'day' | 'actor' | 'subject';

export const AUDIT_SCOPE_MODE_OPTIONS: {
  label: string;
  value: AuditScopeMode;
}[] = [
  { label: 'Por dia', value: 'day' },
  { label: 'Por autor', value: 'actor' },
  { label: 'Por entidade', value: 'subject' },
];

export const AUDIT_SUBJECT_TYPE_LABELS: Record<AuditSubjectType, string> = {
  broker: 'Corretora',
  product: 'Produto',
  policy: 'Apólice',
  'policy-batch': 'Balde de apólices',
  proposal: 'Proposta',
  user: 'Usuário',
  'bulk-operation': 'Operação em lote',
};

/**
 * Tipos oferecidos no filtro "Por entidade".
 *
 * `bulk-operation` fica de fora de propósito: o id dele é um UUID gerado na
 * hora, que ninguém tem como digitar. O rótulo continua no catálogo acima
 * porque a COLUNA precisa exibir esse tipo quando a linha for um resumo de lote.
 */
export const AUDIT_SUBJECT_TYPE_OPTIONS: {
  label: string;
  value: AuditSubjectType;
}[] = (Object.keys(AUDIT_SUBJECT_TYPE_LABELS) as AuditSubjectType[])
  .filter(value => value !== 'bulk-operation')
  .map(value => ({ label: AUDIT_SUBJECT_TYPE_LABELS[value], value }));

/**
 * Rótulos do catálogo de ações, espelhando `AUDIT_ACTION_LABELS` do BFF.
 *
 * Só alimenta o SELECT de filtro. O rótulo exibido em cada linha vem do
 * próprio item (`actionLabel`), congelado na escrita: um registro antigo
 * continua mostrando o texto que valia na época.
 */
export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  'broker.products.bulk.activate': 'Ativou produtos em lote',
  'broker.products.bulk.deactivate': 'Desativou produtos em lote',
  'broker.products.update': 'Alterou produtos da corretora',
  'product.emission-mode.update': 'Alterou o modo de emissão',
  'product.payment-gateway.update': 'Alterou a cobrança pelo gateway',
  'product.payment-methods.update': 'Alterou as formas de pagamento',
  'product.payment-methods.import': 'Importou o catálogo do InsureMO',
  'policy.manual-emission': 'Emitiu apólice manualmente',
  'policy-batch.update': 'Alterou o balde de apólices',
  'policy-batch.bulk-update': 'Alterou o balde em lote',
  'policy-batch.delete': 'Removeu do balde de apólices',
  'proposal.analysis.approve': 'Aprovou proposta em análise',
  'proposal.analysis.reject': 'Recusou proposta em análise',
  'user.create': 'Criou usuário',
  'user.update': 'Alterou usuário',
  'user.deactivate': 'Desativou usuário',
};

/**
 * Ações que a API REALMENTE grava hoje.
 *
 * O catálogo `AUDIT_ACTION_LABELS` espelha o enum do backend, que já nomeia
 * ações cujos consumidores ainda não foram instrumentados. Oferecer todas no
 * filtro faria a tela responder "nada aconteceu" onde a verdade é "não
 * registramos isso" — numa trilha de auditoria, os dois se parecem e só um é
 * honesto.
 *
 * Ao ligar um consumidor novo no backend, acrescente a ação aqui.
 */
const INSTRUMENTED_AUDIT_ACTIONS: AuditAction[] = [
  'broker.products.bulk.activate',
  'broker.products.bulk.deactivate',
  'product.emission-mode.update',
  'product.payment-gateway.update',
  'product.payment-methods.update',
  'product.payment-methods.import',
  'policy-batch.update',
  'policy-batch.bulk-update',
  'policy-batch.delete',
  'proposal.analysis.approve',
  'proposal.analysis.reject',
];

export const AUDIT_ACTION_OPTIONS: { label: string; value: AuditAction }[] =
  INSTRUMENTED_AUDIT_ACTIONS.map(value => ({
    label: AUDIT_ACTION_LABELS[value],
    value,
  })).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

/**
 * Rótulo pt-BR de uma chave de `metadata`.
 *
 * As chaves são camelCase vindas do backend e a tela as exibe em caixa alta —
 * `naoProcessadas` virava "NAOPROCESSADAS". Corrigir só o backend não bastaria:
 * a trilha é append-only, então os registros já gravados continuariam com a
 * chave antiga. O de-para cobre as conhecidas; o resto cai na separação por
 * maiúscula, que já resolve a legibilidade.
 */
const METADATA_KEY_LABELS: Record<string, string> = {
  lote: 'Lote',
  produtos: 'Produtos',
  aplicados: 'Aplicados',
  semMudanca: 'Sem mudança',
  falhas: 'Falhas',
  naoProcessadas: 'Não processadas',
  resultado: 'Resultado',
  ramo: 'Ramo',
  balde: 'Balde',
  alteradas: 'Alteradas',
  produto: 'Produto',
  complianceLiberado: 'Compliance liberado',
  // Edição de usuário (chaves do backend em inglês)
  emailChanged: 'E-mail alterado',
  partyChanged: 'Corretora alterada',
};

export function formatMetadataKey(key: string): string {
  const known = METADATA_KEY_LABELS[key];
  if (known) return known;

  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Valor de `metadata` para a tela: booleano vira Sim/Não em vez de true/false. */
export function formatMetadataValue(value: string | number | boolean): string {
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
}

export interface CollapsedAuditRows {
  rows: AuditActivityItem[];
  /** activityId da linha-cabeçalho → quantos registros ela está escondendo. */
  hiddenByRowId: Record<string, number>;
  /**
   * activityId da linha-cabeçalho → id do lote, para TODO grupo (recolhido ou
   * expandido).
   *
   * `hiddenByRowId` só existe enquanto o grupo está fechado, então sozinho ele
   * não permite oferecer o "recolher" depois de abrir.
   */
  batchHeaderById: Record<string, string>;
}

/** Id da operação que agrupa os registros de um mesmo lote, quando houver. */
export function batchIdOf(item: AuditActivityItem): string | undefined {
  const value = item.metadata?.lote;
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * Recolhe os registros de uma mesma operação em lote numa linha só.
 *
 * Uma ativação em 200 corretoras grava 201 registros: sem isto, a visão do dia
 * vira 201 linhas quase idênticas e esconde tudo o que aconteceu em volta. Os
 * registros individuais continuam existindo — são eles que respondem "o que
 * aconteceu NESTA corretora" — e voltam à tela ao expandir o grupo.
 *
 * A linha-cabeçalho é o resumo da operação quando ele está na página; senão, o
 * primeiro registro do grupo.
 *
 * Limite conhecido: o agrupamento é por página. Um lote partido entre duas
 * páginas aparece como dois grupos — o backend pagina por cursor e não tem como
 * garantir que um lote inteiro caiba na mesma página.
 */
export function collapseBatchActivities(
  items: AuditActivityItem[],
  expandedBatchIds: ReadonlySet<string> = new Set()
): CollapsedAuditRows {
  const groups = new Map<string, AuditActivityItem[]>();
  const order: { batchId?: string; item: AuditActivityItem }[] = [];

  items.forEach(item => {
    const batchId = batchIdOf(item);

    if (!batchId) {
      order.push({ item });
      return;
    }

    const group = groups.get(batchId);
    if (group) {
      group.push(item);
      return;
    }

    groups.set(batchId, [item]);
    order.push({ batchId, item });
  });

  const rows: AuditActivityItem[] = [];
  const hiddenByRowId: Record<string, number> = {};
  const batchHeaderById: Record<string, string> = {};

  order.forEach(entry => {
    if (!entry.batchId) {
      rows.push(entry.item);
      return;
    }

    const group = groups.get(entry.batchId) ?? [entry.item];
    const header =
      group.find(item => item.subjectType === 'bulk-operation') ?? group[0];

    rows.push(header);

    if (group.length > 1) {
      batchHeaderById[header.activityId] = entry.batchId;
    }

    if (expandedBatchIds.has(entry.batchId)) {
      group.filter(item => item !== header).forEach(item => rows.push(item));
      return;
    }

    const hidden = group.length - 1;
    if (hidden > 0) {
      hiddenByRowId[header.activityId] = hidden;
    }
  });

  return { rows, hiddenByRowId, batchHeaderById };
}

/** Rótulo pt-BR do tipo de entidade; devolve o código cru se for desconhecido. */
export function formatSubjectType(subjectType: string): string {
  return (
    AUDIT_SUBJECT_TYPE_LABELS[subjectType as AuditSubjectType] ?? subjectType
  );
}

/** `SYSTEM` são as ações de cron/job, que rodam sem JWT de usuário. */
export function formatActorSource(actorSource: string): string {
  return actorSource === 'SYSTEM' ? 'Sistema' : 'Usuário';
}

const ACTOR_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** O texto é um `sub` do Cognito colado direto, e não um nome a buscar? */
export function isAuditActorId(value: string): boolean {
  return ACTOR_ID_PATTERN.test(value.trim());
}

/** Quem aparece na coluna Autor: nome, e-mail ou, em último caso, o `sub`. */
export function formatActor(item: {
  actorName?: string;
  actorEmail?: string;
  actorSub: string;
}): string {
  return item.actorName?.trim() || item.actorEmail?.trim() || item.actorSub;
}

/** `Modo de emissão: Integração -> Manual`, ou só `Produtos: 140001`. */
export function formatChange(change: AuditActivityChange): string {
  const after = change.after ?? '';

  if (change.before) {
    return `${change.label}: ${change.before} → ${after}`;
  }

  return `${change.label}: ${after}`;
}

/**
 * Resumo de uma linha da tabela: a frase livre quando existe, senão as
 * mudanças em sequência. O "e mais N" usa `changesTotal`, que é o total REAL
 * gravado — o item traz no máximo 50 mudanças.
 */
export function formatActivitySummary(item: AuditActivityItem): string {
  if (item.description) {
    return item.description;
  }

  const parts = item.changes.map(formatChange);

  if (item.changesTruncated) {
    parts.push(`… e mais ${item.changesTotal - item.changes.length}`);
  }

  return parts.join(' | ');
}

/** `22/09/2026 14:35` no fuso do navegador. */
export function formatOccurredAt(occurredAt: string): string {
  const date = new Date(occurredAt);

  if (Number.isNaN(date.getTime())) {
    return occurredAt;
  }

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `22/09/2026` a partir de um `YYYY-MM-DD` — sem passar pelo fuso local. */
export function formatDayLabel(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;

  return `${match[3]}/${match[2]}/${match[1]}`;
}

/**
 * Dia padrão do filtro, em UTC.
 *
 * O BFF deriva a chave `day#YYYY-MM-DD` do `occurredAt` em UTC. Como o Brasil
 * é UTC−3, uma ação das 22h de hoje já está indexada no dia seguinte: usar a
 * data LOCAL faria as ações mais recentes sumirem da tela toda noite. Por isso
 * o padrão é a data UTC — e a tela diz isso em voz alta.
 */
export function todayAuditDay(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export interface AuditFiltersState {
  mode: AuditScopeMode;
  day: string;
  actorSub: string;
  subjectType: string;
  subjectId: string;
  action: string;
}

/**
 * Traduz o estado dos filtros nos parâmetros da API, ou `null` quando o
 * recorte ainda está incompleto.
 *
 * O `null` vale ouro: a API devolve 400 sem recorte (e também com
 * `subjectType` sem `subjectId`), então a tela nem dispara a request — mostra
 * o que falta preencher.
 */
export function buildAuditQueryParams(
  filters: AuditFiltersState
): ListAuditActivitiesParams | null {
  const action = filters.action ? (filters.action as AuditAction) : undefined;

  if (filters.mode === 'subject') {
    const subjectId = filters.subjectId.trim();
    if (!filters.subjectType || !subjectId) return null;

    return {
      subjectType: filters.subjectType as AuditSubjectType,
      subjectId,
      action,
    };
  }

  if (filters.mode === 'actor') {
    const actorSub = filters.actorSub.trim();
    if (!actorSub) return null;

    return { actorSub, action };
  }

  if (!filters.day) return null;

  return { day: filters.day, action };
}

/** O que falta preencher para a consulta virar válida. */
export function describeMissingScope(
  filters: AuditFiltersState
): string | null {
  if (buildAuditQueryParams(filters)) return null;

  if (filters.mode === 'subject') {
    if (!filters.subjectType) return 'Escolha o tipo da entidade.';
    return 'Informe o id da entidade (ex.: PTY10000002858042).';
  }

  if (filters.mode === 'actor') {
    return 'Busque o autor pelo nome ou e-mail, ou cole o ID do usuário.';
  }

  return 'Escolha o dia que você quer auditar.';
}

/** Frase do aviso permanente: o usuário precisa saber que NÃO está vendo tudo. */
export function describeActiveScope(
  filters: AuditFiltersState,
  /** Nome do autor já conhecido pela tela; sem ele, cai no ID. */
  actorLabel?: string
): string {
  if (filters.mode === 'subject') {
    const type = formatSubjectType(filters.subjectType);
    return `Mostrando o histórico da entidade ${type} ${filters.subjectId.trim()}. O dia e o autor não se aplicam a este recorte.`;
  }

  if (filters.mode === 'actor') {
    return `Mostrando todas as ações do autor ${actorLabel || filters.actorSub.trim()}, de qualquer dia.`;
  }

  return `Mostrando apenas as ações de ${formatDayLabel(filters.day)} (o dia é contado em UTC). Troque o dia para ver outro período.`;
}

/** Teto de linhas do CSV no BFF (`AUDIT_EXPORT_MAX_ROWS`). */
export const AUDIT_EXPORT_MAX_ROWS = 10_000;

/**
 * O CSV bateu no teto de linhas?
 *
 * O header `X-Audit-Truncated` é a fonte primária, mas ele só é LEGÍVEL pelo
 * navegador se estiver em `Access-Control-Expose-Headers` — hoje o BFF expõe
 * só `x-request-id` e `Content-Disposition`, então cross-origin ele chega como
 * `null`. Nesse caso caímos na contagem de linhas do próprio arquivo: o BFF
 * junta as linhas com CRLF, então contar esse separador é fiel.
 */
export function isExportTruncated(
  truncatedHeader: string | null,
  csv: string
): boolean {
  if (truncatedHeader === 'true') return true;
  if (truncatedHeader === 'false') return false;

  // Sem o header: a última linha não tem separador e a primeira é o cabeçalho,
  // então a contagem de separadores já é o número de linhas de dados.
  const separators = csv.split('\r\n').length - 1;
  return separators >= AUDIT_EXPORT_MAX_ROWS;
}

const UTF8_BOM = '\uFEFF';

/**
 * Garante o BOM UTF-8 no início do CSV que vai para o download.
 *
 * O BFF já manda o arquivo com BOM, mas o `response.text()` o descarta ao
 * decodificar (é o que a especificação do `fetch` manda). Sem ele, o Excel
 * pt-BR abre o arquivo como ANSI e "Ação" vira "AÃ§Ã£o".
 */
export function withUtf8Bom(csv: string): string {
  return csv.startsWith(UTF8_BOM) ? csv : `${UTF8_BOM}${csv}`;
}
